import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/utils/api';

const EMPTY_PULSE = { done: 0, waiting: 0, blocked: 0, to_sign: 0, since: null, window_hours: 24 };

/**
 * useAriaPulse — contadores del "pulso proactivo" de ARIA (hechas / esperan tu OK
 * / frenadas) desde el agregador `GET /api/aria/pulse`. Fuente única para el chip
 * de la TopBar y el widget del Home, así ambos muestran el MISMO número.
 *
 * Refresca en background (solo con la pestaña enfocada) para dar sensación de
 * "vivo" sin castigar el rate limit. Devuelve siempre un objeto contable: ante
 * error o mientras carga, los contadores son 0 (nunca undefined).
 */
export function useAriaPulse(companyId, { window: windowHours } = {}) {
  const q = useQuery({
    // eslint-disable-next-line local/no-literal-querykey -- key standalone del pulso ARIA; sin entidad en qk.ts
    queryKey: ['aria-pulse', companyId, windowHours ?? 24],
    queryFn: async () => {
      const qs = windowHours ? `?window=${encodeURIComponent(windowHours)}` : '';
      // El agregador devuelve un objeto plano { done, waiting, blocked, since,
      // window_hours } — NO un envelope { data }, así que se lee directo (sin
      // unwrapData), igual que /api/aria/inbox.
      const res = await apiFetch(`/api/aria/pulse${qs}`);
      return res && typeof res === 'object' ? res : EMPTY_PULSE;
    },
    enabled: !!companyId,
    staleTime: 60 * 1000,
    refetchInterval: 90 * 1000,
    placeholderData: EMPTY_PULSE,
  });

  const data = q.data ?? EMPTY_PULSE;
  return {
    ...q,
    pulse: data,
    // `to_sign` (partes diarios pendientes de firma, N-FE3) NO se suma al total:
    // es un eje aparte (supervisión), señalado con un punto ámbar en el chip.
    total: (data.done || 0) + (data.waiting || 0) + (data.blocked || 0),
    toSign: data.to_sign || 0,
  };
}

export default useAriaPulse;
